import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'
import { log } from '../lib/logger'

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
const STATUS_OPTS = ['Aberto', 'Em andamento', 'Concluído']
const ANO_ATUAL = new Date().getFullYear()
const MES_ATUAL = new Date().getMonth() + 1
const MES_ANTERIOR = MES_ATUAL === 1 ? 12 : MES_ATUAL - 1

export default function Fcas() {
  const { isAdmin, isGestor, isCoordenador, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const [searchParams] = useSearchParams()
  const [fcas, setFcas] = useState([])
  const [indicadores, setIndicadores] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroStatus, setFiltroStatus] = useState('todos')
  const [filtroMes, setFiltroMes] = useState(searchParams.get('mes') ? Number(searchParams.get('mes')) : MES_ANTERIOR)
  const [filtroIndicador, setFiltroIndicador] = useState(searchParams.get('indicador') || '')
  const [expandedId, setExpandedId] = useState(null)
  const [saving, setSaving] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ indicador_id: '', ano: ANO_ATUAL, mes: MES_ATUAL })
  const [creating, setCreating] = useState(false)
  const [erroForm, setErroForm] = useState(false)
  const [formResultado, setFormResultado] = useState(null)

  useEffect(() => { fetchAll() }, [selectedCompany])

  useEffect(() => {
    if (!form.indicador_id || !showForm) { setFormResultado(null); return }
    supabase.from('resultados')
      .select('meta, resultado')
      .eq('indicador_id', form.indicador_id)
      .eq('mes', form.mes)
      .eq('ano', form.ano)
      .maybeSingle()
      .then(({ data }) => setFormResultado(data || null))
  }, [form.indicador_id, form.mes, form.ano, showForm])

  async function fetchAll() {
    let fcasQ = supabase
      .from('fcas')
      .select('*, indicadores(nome, objetivo, unidade, gestor_id, profiles(id, nome))')
      .order('ano', { ascending: false })
      .order('mes', { ascending: false })
    let indsQ = supabase.from('indicadores').select('id, nome, profiles(nome)').order('nome')
    if (isSuperAdmin && selectedCompany) {
      fcasQ = fcasQ.eq('company_id', selectedCompany.id)
      indsQ = indsQ.eq('company_id', selectedCompany.id)
    }
    const [{ data: fcasData }, { data: indsData }] = await Promise.all([fcasQ, indsQ])

    setIndicadores(indsData || [])

    if (!fcasData || fcasData.length === 0) {
      setFcas([])
      setLoading(false)
      return
    }

    const indicadorIds = [...new Set(fcasData.map(f => f.indicador_id))]
    const { data: resultadosData } = await supabase
      .from('resultados')
      .select('indicador_id, ano, mes, meta, resultado')
      .in('indicador_id', indicadorIds)

    // Numera cada FCA sequencialmente dentro do mesmo indicador/mês/ano (pelo id crescente)
    const sorted = [...fcasData].sort((a, b) => a.id - b.id)
    const counters = {}
    const numeros = {}
    sorted.forEach(f => {
      const key = `${f.indicador_id}-${f.ano}-${f.mes}`
      counters[key] = (counters[key] || 0) + 1
      numeros[f.id] = counters[key]
    })

    setFcas(fcasData.map(f => ({
      ...f,
      numero_mes: numeros[f.id],
      resultado_ref: resultadosData?.find(r =>
        r.indicador_id === f.indicador_id && r.ano === f.ano && r.mes === f.mes
      ) || null
    })))
    setLoading(false)
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (!form.indicador_id) { setErroForm(true); return }
    setErroForm(false)
    setCreating(true)

    const { data: nova } = await supabase.from('fcas')
      .insert({ indicador_id: Number(form.indicador_id), ano: form.ano, mes: form.mes, status: 'Aberto' })
      .select('*, indicadores(nome, objetivo, unidade, gestor_id, profiles(id, nome))')
      .single()

    if (nova) {
      log('fca_criado', 'fca', nova.id, {
        indicador_id: nova.indicador_id,
        indicador: nova.indicadores?.nome,
        mes: nova.mes,
        ano: nova.ano,
      })
      setFcas(prev => {
        const numero_mes = prev.filter(f =>
          f.indicador_id === nova.indicador_id && f.ano === nova.ano && f.mes === nova.mes
        ).length + 1
        return [{ ...nova, numero_mes, resultado_ref: null, quando: null, agenda_critica: null, conclusao: null }, ...prev]
      })
      setExpandedId(nova.id)
      setFiltroIndicador(String(nova.indicador_id))
      setFiltroMes(nova.mes)
      setFiltroStatus('todos')
    }
    setShowForm(false)
    setCreating(false)
  }

  async function saveFca(id, campos) {
    setSaving(id)
    await supabase.from('fcas').update(campos).eq('id', id)
    setFcas(prev => prev.map(f => f.id === id ? { ...f, ...campos } : f))
    setSaving(null)
  }

  async function deleteFca(id) {
    await supabase.from('fcas').delete().eq('id', id)
    setFcas(prev => prev.filter(f => f.id !== id))
    if (expandedId === id) setExpandedId(null)
  }

  const hoje = new Date().toISOString().slice(0, 10)

  function isAtrasado(f) {
    if (f.status === 'Concluído') return false
    const atrasadoPorAcao = f.quando && f.quando < hoje && f.status === 'Aberto'
    const atrasadoPorAnalise = f.agenda_critica && f.agenda_critica < hoje && !f.resultado_acao
    return atrasadoPorAcao || atrasadoPorAnalise
  }

  // Gestores únicos presentes nas FCAs (para o filtro)
  const gestores = (isAdmin || isSuperAdmin)
    ? [...new Map(
        fcas
          .filter(f => f.indicadores?.profiles)
          .map(f => [f.indicadores.profiles.id, f.indicadores.profiles])
      ).values()].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
    : []

  // Filtro compartilhado com as outras telas. Aqui a lista sai das FCAs
  // carregadas, entao um gestor sem nenhuma FCA nao aparece — nesse caso esta
  // tela mostra todas, sem perder a selecao nas demais (ver lib/filtroGestor).
  // Quem nao tem o seletor tambem nao tem o recorte, pelo mesmo motivo.
  const podeFiltrarGestor = isAdmin || isSuperAdmin
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, gestores) : ''

  const totalAtrasados = fcas.filter(isAtrasado).length

  const filtered = fcas.filter(f => {
    if (filtroStatus === 'atrasados' && !isAtrasado(f)) return false
    if (filtroStatus !== 'todos' && filtroStatus !== 'atrasados' && f.status !== filtroStatus) return false
    if (gestorSel && f.indicadores?.profiles?.id !== gestorSel) return false
    if (filtroMes && f.mes !== Number(filtroMes)) return false
    if (filtroIndicador && f.indicador_id !== Number(filtroIndicador)) return false
    return true
  })

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>FCAs</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            Fato, Causa e Ação — metas não atingidas
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => {
          if (!showForm) setForm({ indicador_id: filtroIndicador || '', ano: ANO_ATUAL, mes: filtroMes ? Number(filtroMes) : MES_ATUAL })
          setShowForm(s => !s)
        }}>
          {showForm ? 'Cancelar' : '+ Nova FCA'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 14 }}>Nova FCA</h3>
          <form onSubmit={handleCreate}>
            <div className="row">
              <div className="col">
                <label>Indicador</label>
                <select
                  value={form.indicador_id}
                  onChange={e => { setForm(f => ({ ...f, indicador_id: e.target.value })); setErroForm(false) }}
                  style={erroForm ? { borderColor: 'var(--red)' } : {}}
                >
                  <option value="">Selecione o indicador...</option>
                  {indicadores.map(i => (
                    <option key={i.id} value={i.id}>{i.nome}</option>
                  ))}
                </select>
                {erroForm && <span style={{ fontSize: 12, color: 'var(--red)', marginTop: 4, display: 'block' }}>Selecione um indicador.</span>}
              </div>
              <div className="col" style={{ maxWidth: 120 }}>
                <label>Mês</label>
                <select value={form.mes} onChange={e => setForm(f => ({ ...f, mes: Number(e.target.value) }))}>
                  {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="col" style={{ maxWidth: 100 }}>
                <label>Ano</label>
                <select value={form.ano} onChange={e => setForm(f => ({ ...f, ano: Number(e.target.value) }))}>
                  {[ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1].map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Criando...' : 'Criar FCA'}
                </button>
              </div>
            </div>

            {form.indicador_id && (() => {
              const ind = indicadores.find(i => String(i.id) === String(form.indicador_id))
              if (!formResultado) return (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text3)' }}>
                  Sem dados de resultado para {ind?.nome} em {MESES[form.mes - 1]}/{form.ano}.
                </div>
              )
              const isOk = ind?.objetivo === 'maior'
                ? formResultado.resultado >= formResultado.meta
                : formResultado.resultado <= formResultado.meta
              return (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text2)' }}>{ind?.nome} · {MESES[form.mes - 1]}/{form.ano}</span>
                  <span>Meta: <strong style={{ fontFamily: 'var(--mono)' }}>{Number(formResultado.meta).toFixed(2)}</strong></span>
                  <span>Resultado: <strong style={{ fontFamily: 'var(--mono)', color: isOk ? 'var(--green)' : 'var(--red)' }}>{Number(formResultado.resultado).toFixed(2)}</strong></span>
                  <span className={`badge ${isOk ? 'badge-green' : 'badge-red'}`}>{isOk ? 'OK' : 'NOK'}</span>
                </div>
              )
            })()}
          </form>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Status */}
        <div style={{ display: 'flex', gap: 4 }}>
          {['todos', 'Aberto', 'Em andamento', 'Concluído'].map(s => (
            <button key={s} className={`btn btn-sm ${filtroStatus === s ? 'btn-primary' : ''}`}
              onClick={() => setFiltroStatus(s)}>
              {s === 'todos' ? `Todos (${fcas.length})` : s}
            </button>
          ))}
          <button
            className="btn btn-sm"
            onClick={() => setFiltroStatus('atrasados')}
            style={filtroStatus === 'atrasados'
              ? { background: 'var(--red)', color: '#fff', borderColor: 'var(--red)' }
              : { color: 'var(--red)', borderColor: 'var(--red-border)' }}
          >
            Atrasados {totalAtrasados > 0 && `(${totalAtrasados})`}
          </button>
        </div>

        <div style={{ width: 1, height: 24, background: 'var(--border)' }} />

        {/* Mês */}
        <select value={filtroMes} onChange={e => setFiltroMes(e.target.value ? Number(e.target.value) : '')} style={{ width: 'auto' }}>
          <option value="">Todos os meses</option>
          {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>

        {/* Indicador */}
        <select value={filtroIndicador} onChange={e => setFiltroIndicador(e.target.value)} style={{ width: 'auto', minWidth: 180 }}>
          <option value="">Todos os indicadores</option>
          {indicadores.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
        </select>

        {/* Gestor — admin/super_admin only */}
        {podeFiltrarGestor && (
          <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
            <option value="">Todos os gestores</option>
            {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
        )}

        <button className="btn btn-sm" onClick={() => { setFiltroMes(''); setFiltroGestor(''); setFiltroIndicador(''); setFiltroStatus('todos') }}>
          Limpar filtros
        </button>
      </div>

      {loading ? (
        <div className="empty-state">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          {fcas.length === 0
            ? 'Nenhum FCA registrado. Clique em "+ Nova FCA" para registrar.'
            : 'Nenhum FCA encontrado com os filtros selecionados.'}
        </div>
      ) : (
        filtered.map(fca => (
          <FcaCard key={fca.id} fca={fca} expanded={expandedId === fca.id}
            onToggle={() => setExpandedId(expandedId === fca.id ? null : fca.id)}
            onSave={saveFca} onDelete={deleteFca} parentSaving={saving === fca.id} isAdmin={isAdmin} canDelete={isAdmin || isGestor || isCoordenador || isSuperAdmin}
            onFilterAdjust={({ mes }) => setFiltroMes(mes)} />
        ))
      )}
    </div>
  )
}

const AUTO_SAVE_CAMPOS = [
  'mes', 'ano',
  'fato', 'porque_1', 'porque_2', 'porque_3', 'porque_4', 'porque_5',
  'acao', 'como', 'por_que_fazer', 'onde', 'responsavel', 'quando',
  'custo', 'status', 'agenda_critica', 'resultado_acao', 'conclusao',
]
const DATE_CAMPOS = ['quando', 'agenda_critica']

function FcaCard({ fca, expanded, onToggle, onSave, onDelete, parentSaving, isAdmin, canDelete, onFilterAdjust }) {
  const ind = fca.indicadores
  const res = fca.resultado_ref
  const [local, setLocal] = useState(fca)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [erroValidacao, setErroValidacao] = useState(null)
  const [autoSaveStatus, setAutoSaveStatus] = useState(null) // null | 'saving' | 'saved' | 'status-retido'
  const autoSaveTimer = useRef(null)
  const baseline = useRef(fca)
  // Status de antes de a conclusao te-lo levado para "Concluído", para desfazer
  // exatamente o que foi mudado se a pessoa limpar a conclusao.
  const statusAntesConclusao = useRef(null)

  useEffect(() => {
    setLocal(fca)
    baseline.current = fca
  }, [fca])

  // Auto-save com debounce de 2s
  useEffect(() => {
    if (!expanded) return
    const hasChanges = AUTO_SAVE_CAMPOS.some(c => local[c] !== baseline.current[c])
    if (!hasChanges) return

    clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      const campos = Object.fromEntries(AUTO_SAVE_CAMPOS.map(c => [c, local[c]]))

      // O auto-save aplica a mesma regra do botao salvar (ver handleSave):
      // concluir exige "Resultado obtido" e conclusao. Sem isso, tudo e' gravado
      // menos o status, que fica no ultimo valor persistido — senao a FCA
      // entraria como concluida nos analytics sem resultado registrado.
      const podeConcluir = local.resultado_acao?.trim() && local.conclusao
      const statusRetido = campos.status === 'Concluído' && !podeConcluir
      if (statusRetido) campos.status = baseline.current.status

      const dateChanges = {}
      DATE_CAMPOS.forEach(c => {
        if (local[c] !== baseline.current[c]) dateChanges[c] = { de: baseline.current[c], para: local[c] }
      })

      await onSave(fca.id, campos)

      if (local.mes !== baseline.current.mes) onFilterAdjust({ mes: local.mes })

      if (Object.keys(dateChanges).length > 0) {
        log('fca_data_alterada', 'fca', fca.id, {
          indicador: fca.indicadores?.nome,
          mes: fca.mes,
          ano: fca.ano,
          alteracoes: dateChanges,
        })
      }

      baseline.current = { ...baseline.current, ...campos }
      setAutoSaveStatus(statusRetido ? 'status-retido' : 'saved')
      if (!statusRetido) setTimeout(() => setAutoSaveStatus(null), 3000)
    }, 2000)

    return () => clearTimeout(autoSaveTimer.current)
  }, [local, expanded])

  // Escolher a conclusao ja' e' dizer que a acao acabou e o resultado e'
  // conhecido, entao o status acompanha sozinho. Limpar a conclusao devolve o
  // status que estava antes — senao a FCA ficaria "Concluído" sem conclusao,
  // combinacao que o proprio handleSave rejeita.
  function handleConclusaoChange(valor) {
    setLocal(l => {
      if (valor && l.status !== 'Concluído') {
        statusAntesConclusao.current = l.status
        return { ...l, conclusao: valor, status: 'Concluído' }
      }
      if (!valor && l.status === 'Concluído' && statusAntesConclusao.current) {
        const anterior = statusAntesConclusao.current
        statusAntesConclusao.current = null
        return { ...l, conclusao: valor, status: anterior }
      }
      return { ...l, conclusao: valor }
    })
    setErroValidacao(null)
  }

  async function handleSave() {
    if (!local.quando) { setErroValidacao('Preencha o campo "Quando fazer" antes de salvar.'); return }
    if (!local.agenda_critica) { setErroValidacao('Preencha a "Agenda de análise crítica" antes de salvar.'); return }
    if (local.status === 'Concluído' && !local.resultado_acao?.trim()) { setErroValidacao('Preencha o "Resultado obtido" antes de concluir.'); return }
    if (local.status === 'Concluído' && !local.conclusao) { setErroValidacao('Selecione a "Conclusão" (Efetivo ou Não Efetivo) antes de concluir.'); return }
    setErroValidacao(null)
    setSaving(true)
    const campos = {
      fato: local.fato,
      porque_1: local.porque_1, porque_2: local.porque_2, porque_3: local.porque_3,
      porque_4: local.porque_4, porque_5: local.porque_5,
      acao: local.acao, como: local.como, por_que_fazer: local.por_que_fazer,
      onde: local.onde, responsavel: local.responsavel, quando: local.quando,
      custo: local.custo, status: local.status,
      agenda_critica: local.agenda_critica, resultado_acao: local.resultado_acao,
      conclusao: local.conclusao,
    }
    await onSave(fca.id, campos)
    const alteracoes = {}
    const rastrear = ['fato', 'status', 'acao', 'responsavel', 'quando', 'conclusao', 'resultado_acao']
    rastrear.forEach(f => { if (local[f] !== fca[f]) alteracoes[f] = { de: fca[f], para: local[f] } })
    log('fca_atualizado', 'fca', fca.id, {
      indicador: fca.indicadores?.nome,
      mes: fca.mes,
      ano: fca.ano,
      alteracoes,
    })
    setSaving(false)
    setSaved(true)
    setTimeout(() => {
      setSaved(false)
      onToggle()
    }, 1500)
  }

  // Calcula desvio da meta
  const temDados = res && res.meta != null && res.resultado != null
  const desvioPct = temDados
    ? (((res.resultado - res.meta) / res.meta) * 100).toFixed(1)
    : null
  const desvioPctNum = temDados ? Number(desvioPct) : null
  const desvioPctStr = desvioPctNum != null
    ? (desvioPctNum > 0 ? `+${desvioPct}%` : `${desvioPct}%`)
    : null

  return (
    <div className="card" style={{ marginBottom: 12, padding: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '12px 16px' }}
        onClick={onToggle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>#{fca.numero_mes}</span>
            <span style={{ fontWeight: 600 }}>{ind?.nome}</span>
            <span className="badge badge-gray">{MESES[fca.mes - 1]}/{fca.ano}</span>
            <span className={`badge ${fca.status === 'Concluído' ? 'badge-green' : fca.status === 'Em andamento' ? 'badge-amber' : 'badge-red'}`}>
              {fca.status}
            </span>
            {parentSaving && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Salvando...</span>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--text2)', flexWrap: 'wrap' }}>
            <span>Gestor: <strong>{ind?.profiles?.nome}</strong></span>
            {temDados && (
              <>
                <span style={{ color: 'var(--border2)' }}>·</span>
                <span>
                  Meta: <strong style={{ fontFamily: 'var(--mono)' }}>{Number(res.meta).toFixed(2)}</strong>
                </span>
                <span style={{ color: 'var(--border2)' }}>·</span>
                <span>
                  Realizado: <strong style={{ fontFamily: 'var(--mono)', color: 'var(--red)' }}>{Number(res.resultado).toFixed(2)}</strong>
                </span>
                <span style={{ color: 'var(--border2)' }}>·</span>
                <span>
                  Desvio: <strong style={{
                    fontFamily: 'var(--mono)',
                    color: ind?.objetivo === 'maior'
                      ? (desvioPctNum >= 0 ? 'var(--green)' : 'var(--red)')
                      : (desvioPctNum <= 0 ? 'var(--green)' : 'var(--red)')
                  }}>{desvioPctStr}</strong>
                </span>
              </>
            )}
            {fca.fato && (
              <>
                <span style={{ color: 'var(--border2)' }}>·</span>
                <span style={{ color: 'var(--text3)' }}>
                  {fca.fato.substring(0, 60)}{fca.fato.length > 60 ? '…' : ''}
                </span>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
          {canDelete && (
            <div onClick={e => e.stopPropagation()}>
              {confirming ? (
                <>
                  <span style={{ fontSize: 12, color: 'var(--text2)', marginRight: 6 }}>Excluir esta FCA?</span>
                  <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff', borderColor: 'var(--red)', marginRight: 4 }}
                    onClick={() => onDelete(fca.id)}>Sim, excluir</button>
                  <button className="btn btn-sm" onClick={() => setConfirming(false)}>Cancelar</button>
                </>
              ) : (
                <button className="btn btn-sm" onClick={() => setConfirming(true)}>Excluir</button>
              )}
            </div>
          )}
          <span style={{ color: 'var(--text3)', fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '16px 16px' }}>
          <div className="row" style={{ marginBottom: 14 }}>
            <div className="col" style={{ maxWidth: 140 }}>
              <label>Mês de referência</label>
              <select value={local.mes} onChange={e => setLocal(l => ({ ...l, mes: Number(e.target.value) }))}>
                {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="col" style={{ maxWidth: 110 }}>
              <label>Ano</label>
              <select value={local.ano} onChange={e => setLocal(l => ({ ...l, ano: Number(e.target.value) }))}>
                {[ANO_ATUAL - 2, ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1].map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Fato</label>
            <textarea value={local.fato || ''} onChange={e => setLocal(l => ({ ...l, fato: e.target.value }))}
              placeholder="Descreva o fato observado..." />
          </div>

          {/* 5 Porquês */}
          <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <label style={{ marginBottom: 10, fontSize: 13, fontWeight: 600 }}>Causa — 5 porquês</label>
            {[1, 2, 3, 4, 5].map(n => (
              <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#fffbeb', border: '1px solid #fde68a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 600, color: '#92400e', flexShrink: 0
                }}>{n}</div>
                <input value={local[`porque_${n}`] || ''}
                  onChange={e => setLocal(l => ({ ...l, [`porque_${n}`]: e.target.value }))}
                  placeholder={`Por quê ${n}?`} />
              </div>
            ))}
          </div>

          {/* Plano de Ação */}
          <h3 style={{ marginBottom: 12 }}>Plano de ação</h3>
          <div className="row">
            <div className="col">
              <label>Ação (o que será feito)</label>
              <textarea value={local.acao || ''} onChange={e => setLocal(l => ({ ...l, acao: e.target.value }))}
                placeholder="Descreva a ação..." style={{ minHeight: 128 }} />
            </div>
            <div className="col">
              <label>Como fazer</label>
              <textarea value={local.como || ''} onChange={e => setLocal(l => ({ ...l, como: e.target.value }))}
                placeholder="Método, processo..." style={{ minHeight: 128 }} />
            </div>
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <div className="col">
              <label>Por que fazer</label>
              <textarea value={local.por_que_fazer || ''} onChange={e => setLocal(l => ({ ...l, por_que_fazer: e.target.value }))}
                placeholder="Justificativa..." style={{ minHeight: 128 }} />
            </div>
            <div className="col">
              <label>Onde fazer</label>
              <input value={local.onde || ''} onChange={e => setLocal(l => ({ ...l, onde: e.target.value }))}
                placeholder="Local, setor..." />
            </div>
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <div className="col">
              <label>Responsável</label>
              <input value={local.responsavel || ''} onChange={e => setLocal(l => ({ ...l, responsavel: e.target.value }))}
                placeholder="Nome..." />
            </div>
            <div className="col">
              <label style={erroValidacao && !local.quando ? { color: 'var(--red)' } : {}}>Quando fazer</label>
              <input type="date" value={local.quando || ''} onChange={e => { setLocal(l => ({ ...l, quando: e.target.value })); setErroValidacao(null) }}
                style={erroValidacao && !local.quando ? { borderColor: 'var(--red)' } : {}} />
            </div>
            <div className="col">
              <label>Quanto custa</label>
              <input value={local.custo || ''} onChange={e => setLocal(l => ({ ...l, custo: e.target.value }))}
                placeholder="R$ 0,00" />
            </div>
            <div className="col">
              <label>Status da ação</label>
              <select value={local.status} onChange={e => setLocal(l => ({ ...l, status: e.target.value }))}>
                {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="col">
              <label style={erroValidacao && !local.agenda_critica ? { color: 'var(--red)' } : {}}>Agenda de análise crítica</label>
              <input type="date" value={local.agenda_critica || ''} onChange={e => { setLocal(l => ({ ...l, agenda_critica: e.target.value })); setErroValidacao(null) }}
                style={erroValidacao && !local.agenda_critica ? { borderColor: 'var(--red)' } : {}} />
            </div>
            <div className="col">
              <label style={erroValidacao && local.status === 'Concluído' && !local.resultado_acao?.trim() ? { color: 'var(--red)' } : {}}>Resultado obtido</label>
              <textarea value={local.resultado_acao || ''} onChange={e => { setLocal(l => ({ ...l, resultado_acao: e.target.value })); setErroValidacao(null) }}
                placeholder="Descreva o resultado..." style={{ minHeight: 128, ...(erroValidacao && local.status === 'Concluído' && !local.resultado_acao?.trim() ? { borderColor: 'var(--red)' } : {}) }} />
            </div>
            <div className="col">
              <label style={erroValidacao && local.status === 'Concluído' && !local.conclusao ? { color: 'var(--red)' } : {}}>Conclusão</label>
              <select value={local.conclusao || ''} onChange={e => handleConclusaoChange(e.target.value)}
                style={erroValidacao && local.status === 'Concluído' && !local.conclusao ? { borderColor: 'var(--red)' } : {}}>
                <option value="">Selecione...</option>
                <option value="Efetivo">Efetivo</option>
                <option value="Não Efetivo">Não Efetivo</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              {autoSaveStatus === 'saving' && '💾 Salvando...'}
              {autoSaveStatus === 'saved' && `💾 Salvo automaticamente às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
              {autoSaveStatus === 'status-retido' && (
                <span style={{ color: 'var(--amber)' }}>
                  💾 Salvo — a ação só entra como concluída com "Resultado obtido" e "Conclusão" preenchidos.
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {erroValidacao && <span style={{ fontSize: 13, color: 'var(--red)' }}>⚠ {erroValidacao}</span>}
              {saved && <span style={{ fontSize: 13, color: 'var(--green)' }}>Salvo com sucesso!</span>}
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar e fechar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
